/**
 * PostLoginLoader Component
 * 
 * Premium loading card that appears after successful login.
 * Matches the exact design from the provided image.
 * 
 * Design:
 * - Light gray background
 * - White rounded card in center
 * - WJS 365ᵈ Together header (blue)
 * - Central gear icon with gradient background
 * - Side icons (briefcase, shield, person)
 * - Hebrew loading messages
 * - Progress bar with gradient and dots
 */

import React from 'react';
import { FiSettings, FiBriefcase, FiShield, FiUser } from 'react-icons/fi';
import { useLang } from '../../contexts/LanguageContext';

type PostLoginLoaderProps = {
  visible: boolean;
};

const PostLoginLoader: React.FC<PostLoginLoaderProps> = ({ visible }) => {
  const { t } = useLang();
  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-gray-200 transition-opacity duration-300"
    >
      {/* White Rounded Card */}
      <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-md w-full mx-4">
        {/* Header: WJS 365ᵈ Together */}
        <div className="text-center mb-8">
          <h1 className="text-blue-600 text-2xl md:text-3xl font-bold">
            WJS 365ᵈ Together
          </h1>
        </div>

        {/* Icons Section */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {/* Left: Two stacked purple icons */}
          <div className="flex flex-col gap-2">
            {/* Briefcase/Document icon */}
            <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center shadow-md">
              <FiBriefcase className="text-white text-xl" />
            </div>
            {/* Shield/Lock icon */}
            <div className="w-12 h-12 rounded-xl bg-purple-600 flex items-center justify-center shadow-md">
              <FiShield className="text-white text-xl" />
            </div>
          </div>

          {/* Center: Large gradient gear icon */}
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 flex items-center justify-center shadow-xl">
            <FiSettings className="text-white text-3xl md:text-4xl animate-spin-slow" />
          </div>

          {/* Right: Light blue person icon */}
          <div className="w-12 h-12 rounded-xl bg-blue-300 flex items-center justify-center shadow-md">
            <FiUser className="text-blue-700 text-xl" />
          </div>
        </div>

        {/* Loading Messages */}
        <div className="text-center mb-8 space-y-2">
          <p className="text-gray-800 text-lg md:text-xl font-medium">
            {t('auth.postLoginMain')}
          </p>
          <p className="text-gray-500 text-sm md:text-base">
            {t('auth.postLoginSub')}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          {/* Progress bar track */}
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-purple-500 via-blue-500 to-blue-600 animate-progress"
              style={{
                width: '60%',
                animation: 'progress 2s ease-in-out infinite',
              }}
            />
          </div>
          
          {/* Three dots below progress bar */}
          <div className="flex items-center justify-center gap-2 mt-3">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes progress {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-progress {
          animation: progress 2s ease-in-out infinite;
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default PostLoginLoader;

